import type { TSDateYMDString } from 'src/types/datetime';
import { isNil } from 'es-toolkit';
import parsePhoneNumber from 'libphonenumber-js';
import { DateTime } from 'luxon';
import { SalesforceActivitySubtype, SalesforceActivityType } from 'src/types/salesforce-account';

import type { DialpadEventItem, SalesforceActivityHistoryItem, SalesforceActivityItem } from './salesforce/activity';
import {
  isDeftSalesSMS,
  isDialpadEvent,
  isEmailMessage,
  isEvent,
  isNote,
  isSalesforceActivityHistory,
  isTask,
} from './salesforce/activity';

// TODO: consider removing unless we use for heldaway holdings
export function formatDollarAmount(amountRaw: string | number, showDecimal?: boolean) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',

    minimumFractionDigits: showDecimal ? 2 : 0,
    maximumFractionDigits: showDecimal ? 5 : 0,
  });

  return formatter.format(Number(amountRaw));
}

export function formatPercentage(amountRaw: string | number, decimalPlaces = 2) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });

  return formatter.format(Number(amountRaw) / 100);
}

// Allow for leading 0s
function minimumIntegerDigits(amountRaw: string) {
  if (!amountRaw.startsWith('0')) {
    return undefined;
  }
  const dotIndex = amountRaw.indexOf('.');
  const hasDecimal = dotIndex !== -1;
  return (() => {
    if (dotIndex === 0) {
      return 1;
    }

    if (hasDecimal) {
      return dotIndex;
    }

    return amountRaw.length;
  })();
}

export function formatManualDollarAmountEntry(amountRaw?: string, opts?: { minimumFractionDigits?: number }) {
  if (!amountRaw) {
    return '';
  }

  const firstDotIndex = amountRaw.indexOf('.');
  const dotIndex = amountRaw.lastIndexOf('.');
  const hasDecimal = dotIndex !== -1;
  const hasMoreThan2Decimals = dotIndex > 0 && dotIndex === amountRaw.length - 4;

  let amount = amountRaw;

  if (firstDotIndex !== dotIndex) {
    amount = amount.slice(0, firstDotIndex);
  }

  amount = hasMoreThan2Decimals ? amount.slice(0, amount.length - 1) : amount;

  const hasTrailingDecimal = dotIndex === amount.length - 1;
  const hasTrailing0 = hasDecimal && amount.endsWith('0') && dotIndex === amount.length - 2;

  const hasTrailingX0 = hasDecimal && amount.endsWith('0') && dotIndex === amount.length - 3;

  let defaultMinimumFractionDigits = 0;
  if (hasTrailing0) {
    defaultMinimumFractionDigits = 1;
  }
  if (hasTrailingX0) {
    defaultMinimumFractionDigits = 2;
  }

  const formatter = new Intl.NumberFormat('en-US', {
    minimumIntegerDigits: minimumIntegerDigits(amount),
    minimumFractionDigits: opts?.minimumFractionDigits || defaultMinimumFractionDigits,
    maximumFractionDigits: 2,
  });
  const formattedAmount = formatter.format(Number(amount));

  if (hasTrailingDecimal) {
    return `${formattedAmount}.`;
  }
  return formattedAmount;
}

export function digitsOnly(s: string | null | undefined): string {
  return (s ?? '').replace(/\D/g, '');
}

export function shortSSN(ssn: string | null | undefined): string {
  if (!ssn) {
    return '';
  }

  const cleaned = ssn.replace(/\D/g, '');
  if (cleaned.length < 4) {
    return '';
  }

  return cleaned.slice(-4);
}

export function formatSSN(ssn: string, blur: boolean) {
  let fullSSN = ssn.replace(/\D/g, '');
  if (blur) {
    const exedOutNumbers = fullSSN.substring(0, 5).replace(/[0-9]/g, '•');
    fullSSN = exedOutNumbers + fullSSN.substring(5, 9);
  }

  let formattedSSN = '';
  if (fullSSN.length > 3 && fullSSN.length < 6) {
    formattedSSN += `${fullSSN.substring(0, 3)}-`;
    fullSSN = fullSSN.substring(3);
  }
  if (fullSSN.length > 5) {
    formattedSSN += `${fullSSN.substring(0, 3)}-`;
    formattedSSN += `${fullSSN.substring(3, 5)}-`;
    fullSSN = fullSSN.substring(5);
  }
  formattedSSN += fullSSN;
  return formattedSSN.substring(0, 11);
}

export function maskSSN(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 0) return '';
  return `***-**-${digits.slice(-4)}`;
}

export function formatEIN(ein: string, blur: boolean) {
  let fullEIN = ein.replace(/\D/g, '');
  if (blur) {
    const exedOutNumbers = fullEIN.substring(0, 5).replace(/[0-9]/g, '•');
    fullEIN = exedOutNumbers + fullEIN.substring(5, 9);
  }

  let formattedEIN = '';
  if (fullEIN.length > 2) {
    formattedEIN += `${fullEIN.substring(0, 2)}-`;
    fullEIN = fullEIN.substring(2);
  }
  formattedEIN += fullEIN;
  return formattedEIN.substring(0, 10);
}

export function maskEIN(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 0) return '';
  return `**-***${digits.slice(-4)}`;
}

const EIN_PATTERN = /^\d{2}-\d{7}$/;
export const EIN_ERROR_MESSAGE = 'Enter a valid 9-digit EIN (XX-XXXXXXX).';

// EIN is never required, so an empty value is always valid — only a partial or malformed one errors.
export function einError(value: string): string | undefined {
  return value === '' || EIN_PATTERN.test(value) ? undefined : EIN_ERROR_MESSAGE;
}

const NANP_NATIONAL_LENGTH = 10;
const NANP_COUNTRY_CODE = '1';

// Imported numbers often carry the US country code without a leading `+`,
// so strip it before the NANP layout below drops the trailing digit.
function toNationalDigits(phoneNumber: string): string {
  const digits = phoneNumber.replace(/\D/g, '');
  return digits.length === NANP_NATIONAL_LENGTH + 1 && digits.startsWith(NANP_COUNTRY_CODE) ? digits.slice(1) : digits;
}

export function formatPhoneNumber(phoneNumber: string) {
  // Handle phone extensions (separated by ; or ,)
  const extensionRegex = /^([^;,]+)[;,](.+)$/;
  const extensionMatch = extensionRegex.exec(phoneNumber);
  const baseNumber = extensionMatch?.[1] ?? phoneNumber;
  const extension = extensionMatch?.[2] ?? null;

  let formattedBase: string;

  if (isValidE164Format(baseNumber)) {
    formattedBase = parsePhoneNumber(baseNumber)?.formatNational() || baseNumber;
  } else {
    const fullNumber = toNationalDigits(baseNumber);
    if (fullNumber.length === 0) {
      formattedBase = fullNumber;
    } else if (fullNumber.length < 4) {
      formattedBase = fullNumber;
    } else if (fullNumber.length < 7) {
      formattedBase = `(${fullNumber.slice(0, 3)}) ${fullNumber.slice(3)}`;
    } else if (fullNumber.length === 7) {
      formattedBase = `${fullNumber.slice(0, 3)}-${fullNumber.slice(3)}`;
    } else if (fullNumber.length <= NANP_NATIONAL_LENGTH) {
      formattedBase = `(${fullNumber.slice(0, 3)}) ${fullNumber.slice(3, 6)}-${fullNumber.slice(6)}`;
    } else {
      formattedBase = baseNumber;
    }
  }

  if (extension) {
    // Sanitize extension to only allow digits
    const sanitizedExtension = extension.replace(/\D/g, '');
    if (sanitizedExtension) {
      return `${formattedBase} ext. ${sanitizedExtension}`;
    }
  }
  return formattedBase;
}

function isValidE164Format(phoneNumber: string) {
  const regex = /^\+[1-9]\d{10,14}$/;
  return regex.test(phoneNumber);
}

type THouseholdBase = {
  name?: string;
  users?: { firstName?: string; lastName?: string }[];
};
export function formatHouseholdName(household: THouseholdBase | null | undefined) {
  // If there is a custom household name (ie; not "Allison Chan Household")
  if (!household?.name?.trim().toLowerCase().endsWith('household')) {
    return household?.name;
  }

  const users = household.users ?? [];
  // Only display non custom household name for multiple client household
  if (users.length > 1 && household.name.includes('&')) {
    return household.name.replace('Household', '').trim();
  }
  return;
}

export function formatLargeDollarAmount(amount: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumSignificantDigits: 3,
  }).format(amount);
}

export function formatLargeUSDPrecision(value: number, precision: number): string {
  let counter = 0;
  let formattedValue = Math.abs(value);
  const units = ['', 'k', 'm', 'b'];
  while (formattedValue > 1000) {
    counter++;
    formattedValue /= 1000;
  }

  const roundedValue = formattedValue % 1 !== 0 ? formattedValue.toPrecision(precision) : formattedValue.toString();

  return value < 0 ? `-$${roundedValue}${units[counter]}` : `$${roundedValue}${units[counter]}`;
}

export function calculateAgeFromDOB(birthday: TSDateYMDString | DateTime) {
  const dateOfBirth = typeof birthday === 'string' ? DateTime.fromISO(birthday) : birthday;

  return Math.floor(DateTime.now().diff(dateOfBirth, 'years').years);
}

// ------------- //
// -- Strings -- //
// ------------- //

export function snakeCaseToSentenceCase(inputString: string) {
  const spacedString = inputString.replace(/_/g, ' ');
  return spacedString.charAt(0).toUpperCase() + spacedString.slice(1);
}

export function capitalizeFirstLetter(string: string) {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function removeLeadingZero(value: string) {
  if (value.startsWith('0') && value.length > 1 && value[1] !== '.') {
    return value.substring(1);
  }
  return value;
}

export function prependZeroToLeadingDecimal(value: string) {
  return value.startsWith('.') ? `0${value}` : value;
}

/**
 * TODO: Maybe expand this to cover other cases, like camelCase, kebab-case, etc.
 * Converts a snake_case string to a human-readable format.
 *
 * This function transforms strings from snake_case format (e.g., "SENIOR_ADVISOR", "USER_ROLE_ADMIN")
 * into a human-readable format with proper capitalization and spaces (e.g., "Senior Advisor", "User Role Admin").
 *
 * @param snakeCaseString - The input string in snake_case format (e.g., "SENIOR_ADVISOR")
 * @returns A human-readable string with proper capitalization and spaces (e.g., "Senior Advisor")
 *
 * @example
 * snakeCaseToHumanCase("SENIOR_ADVISOR") // returns "Senior Advisor"
 */
export function snakeCaseToHumanCase(snakeCaseString: string) {
  return snakeCaseString
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// ----------- //
// -- Dates -- //
// ----------- //

export function formattedMonthDay(date: string) {
  const dateSplit = date.substring(0, 10).split('-');
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: This rule was recently enabled for the repo
  const monthNumber = dateSplit[1]!;
  const day = dateSplit[2];
  const d = new Date(2000, parseInt(monthNumber) - 1, 1);
  const month = d.toLocaleString('en-US', { month: 'short' });

  return `${month} ${day}`;
}

// Format: Mon dd, yyyy
// Example: Apr 1, 2023
export function formatDateToMediumString(date: DateTime) {
  return date.toFormat('MMM d, yyyy');
}

export function formatDateStringExcludeCurrentYear(dateString: string) {
  const date = DateTime.fromISO(dateString).toLocaleString(DateTime.DATE_MED);
  if (date.includes(new Date().getFullYear().toString())) {
    return date.split(',')[0];
  }
  return date;
}

export function formatDateWithTime(dateString: string) {
  const isoString = DateTime.fromISO(dateString).isValid
    ? dateString
    : (DateTime.fromSQL(dateString).toISO() ?? dateString);

  const dayOfTimestamp = formatDateStringExcludeCurrentYear(isoString);
  const timeOfTimestamp = DateTime.fromISO(isoString).toLocaleString(DateTime.TIME_SIMPLE);

  const formatDateWithTime = `${dayOfTimestamp} at ${timeOfTimestamp.toLowerCase()}`;
  return formatDateWithTime;
}

/**
 * Formats a date with time, preserving the timezone from the ISO string.
 * If the current year matches the date's year, excludes the year.
 * Format: "MMM d at h:mm am/pm" (or "MMM d, yyyy at h:mm am/pm" if different year)
 */
export function formatDateTimeWithYear(dateString: string): string {
  try {
    const dt = DateTime.fromISO(dateString);
    if (!dt.isValid) return '';
    return `${dt.toFormat('MMM d, yyyy')} at ${dt.toFormat('h:mm a').toLowerCase()}`;
  } catch {
    return '';
  }
}

export function formatDateTimeWithOptionalYear(dateString: string): string {
  try {
    const dt = DateTime.fromISO(dateString);
    if (!dt.isValid) return '';

    const year = dt.year;
    const currentYear = DateTime.now().year;
    const dateStr = year === currentYear ? dt.toFormat('MMM d') : dt.toFormat('MMM d, yyyy');
    const timeStr = dt.toFormat('h:mm a').toLowerCase();

    return `${dateStr} at ${timeStr}`;
  } catch {
    return '';
  }
}

export function formatDatePartWithConditionalYear(dateString: string): string {
  try {
    const dt = DateTime.fromISO(dateString);
    if (!dt.isValid) return '';

    const year = dt.year;
    const currentYear = DateTime.now().year;
    return year === currentYear ? dt.toFormat('MMM d') : dt.toFormat('MMM d, yyyy');
  } catch {
    return '';
  }
}

export function formatDateWithTimeAndSeconds(date: DateTime | string) {
  const dateTime = typeof date === 'string' ? DateTime.fromISO(date) : date;
  return dateTime.toFormat('MMM d, yyyy h:mm:ss a');
}

function formatDateForSalesforceActivityHistory(
  activity: SalesforceActivityHistoryItem | (SalesforceActivityItem & SalesforceActivityHistoryItem),
) {
  if (activity.ActivityType === SalesforceActivityType.SMS || isDeftSalesSMS(activity)) {
    const day = formatDateStringExcludeCurrentYear(activity.CreatedDate);
    return day;
  }
  if (activity.Dialpad__CallId__c && activity.Dialpad__Call_End_Time__c) {
    const dayOfCall = formatDateStringExcludeCurrentYear(activity.Dialpad__Call_End_Time__c);

    const callEndTime = DateTime.fromISO(activity.Dialpad__Call_End_Time__c).toLocaleString(DateTime.TIME_SIMPLE);
    const callStartTime = DateTime.fromISO(activity.Dialpad__Call_End_Time__c)
      .minus({ minutes: activity.DurationInMinutes || 0 })
      .toLocaleString(DateTime.TIME_SIMPLE);

    const formattedCallTime =
      callStartTime === callEndTime
        ? `${dayOfCall} at ${callStartTime.toLowerCase()}`
        : `${dayOfCall} at ${callStartTime.split(' ')[0]}-${callEndTime.toLowerCase()}`;

    return formattedCallTime;
  } else if (activity.ActivitySubtype === SalesforceActivitySubtype.Call) {
    const dayOfCall = formatDateStringExcludeCurrentYear(activity.ActivityDate || activity.CreatedDate);
    return dayOfCall;
  } else if (activity.ActivitySubtype === SalesforceActivitySubtype.Task) {
    const dayOfTask = formatDateStringExcludeCurrentYear(activity.CompletedDateTime || activity.CreatedDate);
    const taskTime = DateTime.fromISO(activity.CompletedDateTime || activity.CreatedDate).toLocaleString(
      DateTime.TIME_SIMPLE,
    );
    const taskDateTimeString = activity.CompletedDateTime
      ? `${dayOfTask} at ${taskTime.toLowerCase()}`
      : formatDateStringExcludeCurrentYear(activity.CreatedDate);
    return taskDateTimeString;
  }
  // This case should not happen!
  return activity.ActivityDate || '';
}

export function formatDateForAdvisorDashboard(activity: SalesforceActivityItem) {
  if (isSalesforceActivityHistory(activity)) {
    return formatDateForSalesforceActivityHistory(activity);
  } else if (isEmailMessage(activity)) {
    return formatDateWithTime(activity.MessageDate);
  } else if (isNote(activity)) {
    const dayOfNote = formatDateStringExcludeCurrentYear(activity.createdAt);
    const timeOfNote = DateTime.fromISO(activity.createdAt).toLocaleString(DateTime.TIME_SIMPLE);

    const formattedNoteTimestamp = `${dayOfNote} at ${timeOfNote.toLowerCase()}`;
    return formattedNoteTimestamp;
  } else if (isTask(activity)) {
    const dayOfTask = formatDateStringExcludeCurrentYear(activity.CreatedDate);

    return `Created ${dayOfTask}`;
  } else if (isEvent(activity)) {
    if (activity.StartDateTime || activity.ActivityDateTime) {
      return formatDateStringExcludeCurrentYear(activity.StartDateTime || activity.ActivityDateTime || '');
    }
    return '';
  } else if (isDialpadEvent(activity)) {
    return formatDialpadEventTimestamp(activity);
  }
  return '';
}

function formatDialpadEventTimestamp(activity: DialpadEventItem): string {
  const dateEnded = activity.dateEnded;
  const createdAt = activity.createdAt;

  function isValidString(value: unknown): value is string {
    return typeof value === 'string' && value.length > 0;
  }

  const dateToFormat = (() => {
    if (isValidString(dateEnded)) {
      return dateEnded;
    }

    if (isValidString(createdAt)) {
      return createdAt;
    }

    return null;
  })();

  if (!dateToFormat || typeof dateToFormat !== 'string') {
    return '';
  }

  const dayOfCall = formatDateStringExcludeCurrentYear(dateToFormat);
  const timeOfCall = DateTime.fromISO(dateToFormat).toLocaleString(DateTime.TIME_SIMPLE);

  const formattedCallTimestamp = `${dayOfCall} at ${timeOfCall.toLowerCase()}`;
  return formattedCallTimestamp;
}

// Format: Mon dd, yyyy (for dates more than 1 year ago)
// Format: relative, e.g. 2 days ago, 17 hours ago (for dates less than 1 day ago)
export function formatTimestampToRelative(timestamp: string | undefined) {
  if (!timestamp) {
    return '';
  }
  const dt = DateTime.fromISO(timestamp, { zone: 'utc' });
  const now = DateTime.now();
  const diffInYears = now.diff(dt, 'years').years;
  if (diffInYears > 1) {
    return dt.toFormat('MMM d, yyyy');
  }
  return dt.toRelative();
}

export function formatTimestampToRelativeWithoutAgo(timestamp: string | undefined) {
  const relative_date = formatTimestampToRelative(timestamp);
  return relative_date?.replace(' ago', '');
}

export function formatTimestampToMonthAndDay(timestamp: string | undefined) {
  if (!timestamp) {
    return '';
  }
  const dt = DateTime.fromISO(timestamp, { zone: 'utc' });
  return dt.toFormat('MMM d');
}

export function formatDate(dateString: string) {
  let date = new Date(dateString);

  if (isNaN(date.getTime())) {
    const utcString = dateString.replace(' ', 'T').replace(' UTC', 'Z');
    date = new Date(utcString);
  }
  const month = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(date);
  const day = date.getDate();
  const year = date.getFullYear();

  const formattedDate = `${month} ${day}, ${year}`;
  return formattedDate;
}

export function toTitleCase(str: string) {
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function formatDatetoUSStyle(date: Date | string | null | undefined) {
  if (!date) return '';

  // Callers may hand us either a Date (seeded from the server) or an ISO date
  // string (e.g. the account-confirmation `dob` field, which DueDatePickerField
  // rewrites to a 'yyyy-MM-dd' string via onChange). Normalize before calling
  // Date methods — a raw string would throw `getDate is not a function`.
  const d = date instanceof Date ? date : DateTime.fromISO(date).toJSDate();
  if (Number.isNaN(d.getTime())) return '';

  const day = `0${d.getDate()}`.slice(-2);

  const month = `0${d.getMonth() + 1}`.slice(-2);
  const year = d.getFullYear().toString();
  return `${month}/${day}/${year}`;
}

export function formatDateToYYYYMMDD(date: Date): string {
  const dt = DateTime.fromJSDate(date);
  return dt.toFormat('yyyyMMdd');
}

export function formatDateBasedOnYear(dateString: string) {
  const inputDate = DateTime.fromISO(dateString);
  const now = DateTime.now();

  if (inputDate.year === now.year) {
    return inputDate.toFormat('LLL d');
  }
  return inputDate.toFormat('MM/dd/yy');
}

export function formatDateToAmericanStyle(dateString: string, includeDay = false) {
  const inputDate = DateTime.fromISO(dateString);
  return inputDate.toFormat(includeDay ? 'ccc, LLL d, yyyy' : 'LLL d, yyyy');
}

export function truncateString(str: string, maxLength: number) {
  if (str.length > maxLength) {
    return `${str.substring(0, maxLength - 3)}...`;
  }
  return str;
}

export function formatDateToMMDDYY(dateString: string) {
  const inputDate = DateTime.fromISO(dateString);
  return inputDate.toFormat('MM/dd/yy');
}

/**
 * formatDateToMMDDYYYY returns specific format of a given string date.
 * @param dateString String representation of the date to format
 * @returns String
 */
export function formatDateToMMDDYYYY(dateString: string) {
  const inputDate = DateTime.fromISO(dateString);
  return inputDate.isValid ? inputDate.toFormat('MM/dd/yyyy') : '';
}

/**
 * Formats a date string into a time range with timezone
 *
 * @param dateString - ISO date string (e.g., "2025-04-03T14:00:00Z")
 * @param range - how many hours to add to the start time (default: 1)
 * @returns Formatted time range (e.g., "2:00 - 3:00pm EST")
 *
 * @example
 * "2025-04-03T14:00:00Z" => "2:00 - 3:00pm EST"
 *
 * formatTimeWithRange("2025-04-03T14:00:00Z", 30);
 * returns "2:00 - 2:30pm EST"
 */
export function formatTimeWithRange(dateString: string, range = 1) {
  const inputDate = DateTime.fromISO(dateString);
  const startTime = inputDate.toFormat('h:mm');
  const endTime = inputDate.plus({ hours: range }).toFormat('h:mma').replace('AM', 'am').replace('PM', 'pm');
  const timezone = inputDate.toFormat('ZZZZ');
  return `${startTime} - ${endTime} ${timezone}`;
}

export function formatCityState(city: string | null, state: string | null, zip: string | null) {
  if (!city && !state && !zip) {
    return null;
  } else if (city && state && zip) {
    return `${city}, ${state} ${zip}`;
  } else if (city && state) {
    return `${city}, ${state}`;
  } else if (city && zip) {
    return `${city} ${zip}`;
  } else if (state && zip) {
    return `${state} ${zip}`;
  } else if (city) {
    return city;
  } else if (state) {
    return state;
  } else if (zip) {
    return zip;
  }
  return '';
}

type AddressParts = {
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressLine3?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
};

/** Splits an address into its two display lines: the street lines and the city/state/zip line. */
export function getAddressLines(address: AddressParts): { street: string | null; cityStateZip: string | null } {
  const street = [address.addressLine1, address.addressLine2, address.addressLine3].filter(Boolean).join(', ');
  const cityStateZip = formatCityState(address.city ?? null, address.state ?? null, address.postalCode ?? null);
  return {
    street: street.length > 0 ? street : null,
    cityStateZip: cityStateZip !== null && cityStateZip !== '' ? cityStateZip : null,
  };
}

export function formatFullAddress(address: AddressParts): string | null {
  const { street, cityStateZip } = getAddressLines(address);
  const parts = [street, cityStateZip].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

/**
 * Formats a date's time to a string in the format of "h:mm am/pm"
 */
export function formatTime(date: Date | null, timeZone?: string) {
  if (isNil(date)) return 'N/A';

  if (!isNil(timeZone)) {
    return DateTime.fromJSDate(date).setZone(timeZone).toFormat('h:mma').toLowerCase();
  }

  return date
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    .toLowerCase()
    .replace(' ', '');
}
